import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import {
    FaClipboardList, FaEye, FaPhoneAlt, FaSearch, FaFilter,
    FaCalendarDay, FaExclamationCircle, FaStore, FaCheckCircle, FaClock
} from "react-icons/fa";

export default function ComplaintsPage() {
    const location = useLocation();
    const { backEnd_request } = BackMethods();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [allComplaints, setAllComplaints] = useState([]);
    const [filteredComplaints, setFilteredComplaints] = useState([]);
    const [establishment, setEstablishment] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const query = new URLSearchParams(location.search);

    const truncateWords = (text, limit) => {
        if (!text) return "---";
        const words = text.split(" ");
        if (words.length > limit) return words.slice(0, limit).join(" ") + "...";
        return text;
    };

    const getStatusBadge = (isProcessed) => {
        if (isProcessed) return <span className="badge bg-success-subtle text-success border border-success x-small"><FaCheckCircle className="ms-1" /> معالجة</span>;
        return <span className="badge bg-warning-subtle text-warning border border-warning x-small"><FaClock className="ms-1" /> قيد الانتظار</span>;
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const fieldUid = query.get("establishment");
                if (fieldUid) {
                    const response = await backEnd_request.get(`/public/staff/complaints/by-establishment/${fieldUid}`);
                    const data = response.data;
                    setEstablishment(data.establishment);
                    setAllComplaints(data.complaints || []);
                    setFilteredComplaints(data.complaints || []);
                }
            } catch (err) {
                console.error("Error fetching complaints:", err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [location.search]);

    useEffect(() => {
        let results = allComplaints.filter(c => {
            const matchesSearch = c.submitterPhone.includes(searchTerm) || c.complaintText.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === "all"
                ? true
                : statusFilter === "processed" ? c.isProcessed : !c.isProcessed;

            return matchesSearch && matchesStatus;
        });
        setFilteredComplaints(results);
    }, [searchTerm, statusFilter, allComplaints]);

    return (
        <div className="bg-light min-vh-100 py-3 py-md-4" dir="rtl">
            <div className="container-fluid px-3 px-md-4">

                {/* Header & Stats */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 bg-white p-3 rounded-3 shadow-sm border-bottom border-primary border-3 gap-2">
                    <div>
                        <h4 className="fw-bold text-dark m-0 fs-5 fs-md-4">
                            <FaClipboardList className="ms-2 text-primary" />
                            سجل الشكاوي {establishment && `لـ (${establishment.name})`}
                        </h4>
                    </div>
                    <button onClick={() => navigate(-1)} className="btn btn-outline-dark btn-sm rounded-pill px-4 align-self-start align-self-md-center">رجوع</button>
                </div>

                {/* Filters Section */}
                <div className="row g-2 mb-4">
                    <div className="col-12 col-md-8">
                        <div className="input-group shadow-sm rounded-3 overflow-hidden">
                            <span className="input-group-text bg-white border-0"><FaSearch className="text-muted" /></span>
                            <input
                                type="text"
                                className="form-control border-0 py-2"
                                placeholder="بحث برقم الهاتف أو النص..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-12 col-md-4">
                        <div className="input-group shadow-sm rounded-3 overflow-hidden">
                            <span className="input-group-text bg-white border-0"><FaFilter className="text-muted" /></span>
                            <select
                                className="form-select border-0 py-2"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">كل الحالات</option>
                                <option value="pending">قيد الانتظار</option>
                                <option value="processed">تمت المعالجة</option>
                            </select>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                ) : (
                    <>
                        {/* Desktop Table - Hidden on Mobile */}
                        <div className="table-responsive d-none d-lg-block">
                            <table className="table table-hover align-middle main-modern-table">
                                <thead>
                                    <tr>
                                        <th className="text-center">#</th>
                                        <th>معلومات الشكوى</th>
                                        <th>نص الشكوى</th>
                                        <th>رقم المشتكي</th>
                                        <th>الحالة</th>
                                        <th>التاريخ</th>
                                        <th className="text-center">عرض</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredComplaints.map((c, index) => (
                                        <tr key={c.complaintUid} className="table-row-item">
                                            <td className="text-center fw-bold text-muted">{index + 1}</td>
                                            <td>
                                                <div className="fw-bold text-dark small"><FaStore className="ms-1 text-primary" /> {establishment?.name}</div>
                                                <div className="x-small text-muted">{establishment?.governorate} - {establishment?.city}</div>
                                            </td>
                                            <td style={{ maxWidth: '300px' }}>
                                                <div className="d-flex align-items-start gap-2">
                                                    <FaExclamationCircle className="text-danger mt-1 flex-shrink-0" />
                                                    <div className="small text-dark fw-medium">
                                                        {truncateWords(c.complaintText, 10)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small fw-bold text-primary"><FaPhoneAlt className="ms-1" size={12} /> {c.submitterPhone}</div>
                                            </td>
                                            <td>{getStatusBadge(c.isProcessed)}</td>
                                            <td>
                                                <div className="small fw-bold"><FaCalendarDay className="ms-1 text-info" /> {c.createdAtUtc?.split("T")[0]}</div>
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    className="action-btn view mx-auto"
                                                    onClick={() => navigate(`/admin/complaint-details?uid=${c.complaintUid}`)}
                                                >
                                                    <FaEye />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards - Hidden on Desktop */}
                        <div className="d-lg-none d-flex flex-column gap-3">
                            {filteredComplaints.map((c, index) => (
                                <div key={c.complaintUid} className="card border-0 shadow-sm rounded-3 p-3 position-relative border-start border-primary border-4">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <span className="text-muted fw-bold small">#{index + 1}</span>
                                        {getStatusBadge(c.isProcessed)}
                                    </div>

                                    <div className="mb-2">
                                        <div className="fw-bold text-dark small mb-1"><FaStore className="ms-1 text-primary" /> {establishment?.name}</div>
                                        <div className="x-small text-muted mb-2">{establishment?.governorate} - {establishment?.city}</div>
                                        <div className="p-2 bg-light rounded-2 small text-dark border-end border-danger border-2">
                                            {truncateWords(c.complaintText, 15)}
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                                        <div>
                                            <div className="x-small text-primary fw-bold"><FaPhoneAlt className="ms-1" size={10} /> {c.submitterPhone}</div>
                                            <div className="x-small text-muted mt-1"><FaCalendarDay className="ms-1" size={10} /> {c.createdAtUtc?.split("T")[0]}</div>
                                        </div>
                                        <button
                                            className="btn btn-dark btn-sm rounded-3 px-3 d-flex align-items-center gap-1"
                                            onClick={() => navigate(`/admin/complaint-details?uid=${c.complaintUid}`)}
                                        >
                                            <FaEye size={14} /> تفاصيل
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {filteredComplaints.length === 0 && (
                            <div className="text-center py-5 bg-white rounded-3 shadow-sm mt-3 text-muted">
                                لا توجد نتائج تطابق بحثك
                            </div>
                        )}
                    </>
                )}
            </div>

            <style>{`
                .x-small { font-size: 0.72rem; }
                .main-modern-table { border-collapse: separate; border-spacing: 0 8px; }
                .main-modern-table thead th { border: none; color: #6c757d; font-size: 0.85rem; padding: 12px 15px; }
                .table-row-item { background-color: #fff; box-shadow: 0 2px 5px rgba(0,0,0,0.02); }
                .table-row-item td { padding: 12px 15px; border: none; }
                .table-row-item td:first-child { border-top-right-radius: 10px; border-bottom-right-radius: 10px; }
                .table-row-item td:last-child { border-top-left-radius: 10px; border-bottom-left-radius: 10px; }
                .table-row-item:hover { background-color: #f0f7ff !important; transform: scale(1.005); transition: 0.2s; }
                .action-btn.view { 
                    width: 32px; height: 32px; border-radius: 8px; border: none;
                    background: #1a1d20; color: #fff; display: flex; align-items: center; justify-content: center;
                }
                .form-control:focus, .form-select:focus { box-shadow: none; border: 1px solid #0d6efd; }
                
                @media (max-width: 768px) {
                    .card { transition: 0.2s; }
                    .card:active { transform: scale(0.98); }
                }
            `}</style>
        </div>
    );
}